import { useRef, useState } from 'react';

import { useUploadDocument } from '../../../conversations/hooks/use-upload-document';
import { ConflictAlertSeverityBadge } from '../badges/ConflictAlertSeverityBadge';
import { ConflictAlertTypeBadge } from '../badges/ConflictAlertTypeBadge';

import { ModalBase } from './ModalBase';

import type { ConflictSeverityVM, SupervisorVariant } from '../../view-models';

export interface EvidenceDoc {
  id: string;
  name: string;
}

export interface MitigateAlertModalProps {
  open: boolean;
  alert: { id: string; severity: ConflictSeverityVM; alertType: string } | null;
  variant: SupervisorVariant;
  /** Bond do alerta (`alert.bondsInvolved[0]`); `undefined` desabilita o anexo. */
  bondId: string | undefined;
  /** Mutation em voo — desabilita o submit p/ evitar duplo-POST (review T7). */
  submitting?: boolean;
  onClose: () => void;
  onConfirm: (p: {
    type: string;
    desc: string;
    evidence: EvidenceDoc[];
    notif: { op: boolean; cli: boolean; conv: boolean; gestor: boolean };
  }) => void;
}

// Convertido verbatim de design-references/brief-3/shared/modals-3.jsx:129-222 (§12.6).
export function MitigateAlertModal({
  open,
  alert,
  variant,
  bondId,
  submitting = false,
  onClose,
  onConfirm,
}: MitigateAlertModalProps): React.JSX.Element | null {
  const [type, setType] = useState('cliente-reatribuido');
  const [desc, setDesc] = useState('');
  const [evidence, setEvidence] = useState<EvidenceDoc[]>([]);
  const [notif, setNotif] = useState({ op: true, cli: true, conv: true, gestor: true });
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, isUploading } = useUploadDocument(bondId);
  if (!open || alert === null) return null;
  const docRequired = variant === 'regulated';
  const canAttach = bondId !== undefined;
  const canSubmit =
    desc.trim().length >= 20 &&
    (!docRequired || evidence.length > 0) &&
    !isUploading &&
    !submitting;

  const onPickFile = async (file: File): Promise<void> => {
    setUploadError(null);
    try {
      const doc = await upload(file);
      setEvidence((e) => [...e, { id: doc.id, name: doc.name }]);
    } catch (err) {
      // Não engolir a rejeição — o operador precisa saber que a evidência NÃO anexou
      // (crítico em Regulated, onde documentação é obrigatória). (review T7)
      setUploadError(err instanceof Error ? err.message : 'Falha ao enviar o arquivo.');
    }
  };

  const mitigationTypes = [
    { id: 'op-transferido', label: 'Operador transferido para outra conversa (sem conflito)' },
    { id: 'cliente-reatribuido', label: 'Cliente atribuído a outro operador' },
    { id: 'bond-separado', label: 'Bond foi separado em entidades distintas' },
    { id: 'id-esclarecida', label: 'Identidade compartilhada esclarecida (documento)' },
    { id: 'outro', label: 'Outro (especificar na descrição)' },
  ];

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title="Mitigar alerta de conflito"
      width={620}
      footer={
        <div
          style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'space-between',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-3)' }}>
            {docRequired &&
              (evidence.length === 0
                ? '⚠ Regulated: evidência documental OBRIGATÓRIA'
                : '✓ evidência anexada')}
            {!docRequired && 'evidência opcional (Team-Pro)'}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn-ghost-mini" onClick={onClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-ghost-mini btn-ghost-mini--primary"
              disabled={!canSubmit}
              onClick={() => onConfirm({ type, desc, evidence, notif })}
            >
              Confirmar mitigação
            </button>
          </span>
        </div>
      }
    >
      <div className="modal-form">
        <div className="modal-form__alert-head">
          <ConflictAlertSeverityBadge severity={alert.severity} />
          <ConflictAlertTypeBadge alertType={alert.alertType} />
        </div>

        <fieldset className="modal-form__fieldset">
          <legend>Tipo de mitigação</legend>
          {mitigationTypes.map((opt) => (
            <label key={opt.id} className="modal-form__radio">
              <input
                type="radio"
                name="mit-type"
                value={opt.id}
                checked={type === opt.id}
                onChange={() => setType(opt.id)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </fieldset>

        <label className="modal-form__field">
          <span className="modal-form__label">
            Descrição da mitigação (obrigatória, mínimo 20 caracteres)
          </span>
          <textarea
            className="modal-form__textarea"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Cliente Maria Silva foi reatribuída ao operador Vitoria Lima. Pedro Mendes seguirá atendendo outros clientes. Mitigação definitiva."
            rows={4}
          />
          <span className="modal-form__hint">{desc.trim().length}/20 mínimo</span>
        </label>

        <label className="modal-form__field">
          <span className="modal-form__label">
            Evidência{' '}
            {docRequired ? <b style={{ color: 'var(--danger-fg)' }}>obrigatória</b> : '(opcional)'}
          </span>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            data-testid="mitigate-file-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file !== undefined) void onPickFile(file);
            }}
          />
          <button
            type="button"
            className="btn-ghost-mini modal-form__file"
            disabled={!canAttach || isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            {isUploading ? 'Enviando…' : '+ Adicionar arquivo'}
          </button>
          {!canAttach && (
            <span className="modal-form__hint">anexo requer bond associado ao alerta</span>
          )}
          {uploadError !== null && (
            <span className="modal-form__hint" role="alert" style={{ color: 'var(--danger-fg)' }}>
              {uploadError}
            </span>
          )}
          {evidence.length > 0 && (
            <ul className="modal-form__file-list mono">
              {evidence.map((f, i) => (
                <li key={f.id}>
                  <span>📎 {f.name}</span>
                  <button
                    type="button"
                    className="btn-ghost-mini"
                    onClick={() => setEvidence((e) => e.filter((_, j) => j !== i))}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>

        <fieldset className="modal-form__fieldset">
          <legend>Notificações</legend>
          <label className="modal-form__check">
            <input
              type="checkbox"
              checked={notif.op}
              onChange={(e) => setNotif((n) => ({ ...n, op: e.target.checked }))}
            />{' '}
            Notificar operador envolvido
          </label>
          <label className="modal-form__check">
            <input
              type="checkbox"
              checked={notif.cli}
              onChange={(e) => setNotif((n) => ({ ...n, cli: e.target.checked }))}
            />{' '}
            Notificar cliente envolvido (se apropriado)
          </label>
          <label className="modal-form__check">
            <input
              type="checkbox"
              checked={notif.conv}
              onChange={(e) => setNotif((n) => ({ ...n, conv: e.target.checked }))}
            />{' '}
            Adicionar evento na conversa
          </label>
          <label className="modal-form__check">
            <input
              type="checkbox"
              checked={notif.gestor}
              onChange={(e) => setNotif((n) => ({ ...n, gestor: e.target.checked }))}
            />{' '}
            Notificar gestor
          </label>
        </fieldset>
      </div>
    </ModalBase>
  );
}
